import React, { useCallback, useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";

import { selectReportsByVersionId, useGetAccountReports, useGetVersionStatusQuery } from "fond/api";
import ArchitectureModal from "fond/architecture/Architecture";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { extendedInputLayerGroupList, LayerGroupIds } from "fond/layers";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import LayerPanel from "fond/project/panels/InputPanel/LayerPanel";
import MultiLayerUploadForm from "fond/project/panels/InputPanel/MultiLayerUploadForm";
import { closeArchitectureModal, Modals, openArchitectureModal, StatusTypes } from "fond/project/redux";
import { SolvePanel } from "fond/project/solve";
import { Architecture, LayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";

import ArchitecturePanel from "../ArchitecturePanel";
import AreaSelect from "../AreaSelectPanel";
import Draw from "../DrawPanel";
import ImportData from "../ImportDataPanel";
import InputDataPanel from "../InputDataPanel";
import InputMethodsPanel from "../InputMethodsPanel";
import ConstructionPrintExport from "../MuPower/ConstructionPrintExport";
import PolygonSelect from "../PolygonSelectPanel";
import ReportPanel from "../ReportPanel/ReportPanel";
import UploadPanel from "../UploadPanel";

import DataPrepPanel from "./DataPrep/DataPrepPanel";
import DataPrepSettingsPanel from "./DataPrep/DataPrepSettingsPanel";
import IdentifyStreetParcels from "./DataPrep/IdentifyStreetParcels";
import ModificationsDraw from "./DataPrep/ModificationsDraw";
import ArtefactsPanel from "./ServiceArea/ArtefactsPanel";

export const extendedDesignStackedNavigationPanels: Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }> = [
  {
    id: "addresses",
    name: "Addresses",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.inAddress} />,
  },
  {
    id: "baseDataStreet",
    name: "Streets",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.inBaseDataStreet} />,
  },
  {
    id: "aerialSpanAndPoles",
    name: "Aerial Span and Poles",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.spanPole} />,
  },
  {
    id: "centralOffice",
    name: "Central Office",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.inExchange} />,
  },
  {
    id: "area-select",
    name: "Area Select",
    component: <AreaSelect />,
    useDefaultHeader: false,
  },
  {
    id: "upload",
    name: "Upload",
    component: <UploadPanel />,
    useDefaultHeader: false,
  },
  {
    id: "draw",
    name: "Draw on Map",
    component: <Draw />,
    useDefaultHeader: false,
  },
  {
    id: "draw-polygon",
    name: "Draw Polygon",
    component: <PolygonSelect />,
    useDefaultHeader: false,
  },
  {
    id: "import-data",
    name: "Import data",
    component: <ImportData />,
  },
  {
    id: "use-existing-polygon",
    name: "Use existing polygon",
    component: <PolygonSelect />,
    useDefaultHeader: false,
  },
  {
    id: "dataPrepSettings",
    name: "Data Prep Settings",
    component: <DataPrepSettingsPanel />,
  },
  {
    id: "extended-design-modifications",
    name: "Modify data",
    component: (
      <LayerPanel
        title="Modify data"
        layerKey="extended-design-modifications"
        actions={["draw", "upload"]}
        drawTitle="Draw"
        drawSubtitle="Edit or draw data on the map"
      />
    ),
    useDefaultHeader: false,
  },
  {
    id: "extended-design-modifications-draw",
    name: "Modify data",
    component: <ModificationsDraw />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-modifications-upload",
    name: "Upload Base Data",
    component: (
      <MultiLayerUploadForm
        title="Upload Base Data"
        sections={[
          { layerId: LayerIds.inAddress, displayName: "Address", geometryType: "Point" },
          { layerId: LayerIds.inStreet, displayName: "Underground path", geometryType: "LineString" },
          { layerId: LayerIds.inStreetPoints, displayName: "Underground points", geometryType: "Point" },
          { layerId: LayerIds.inLeadIns, displayName: "Leadins", geometryType: "LineString" },
        ]}
      />
    ),
    useDefaultHeader: false,
  },
  {
    id: "identify-street-parcels",
    name: "Review problematic parcels",
    component: <IdentifyStreetParcels />,
  },
  {
    id: "construction-prints-export",
    name: "Export",
    component: <ConstructionPrintExport />,
    useDefaultHeader: false,
  },
];

interface ExtendedDesignPanelProps {
  /**
   * The current architecture.
   */
  architecture: Architecture | null;
  /**
   * The project currently being viewed / edited
   */
  project: Project;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
}

const ExtendedDesignPanel: React.FC<ExtendedDesignPanelProps> = ({ architecture, project, readOnly }: ExtendedDesignPanelProps) => {
  const dispatch = useAppDispatch();
  const modal = useAppSelector((state) => state.project.modal);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { layerKeys } = useGetPlannerLayers(versionId);
  const { data: versionStatus } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const { workflowState: dataPrepWorkflowState } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.PLANNER_EXTENDED.DATA_PREP });

  useGetAccountReports();
  const versionReports = useAppSelector((store) => selectReportsByVersionId(store, versionId));

  const handleOnArchitectureModalClose = () => dispatch(closeArchitectureModal());
  const handleOnChangeArchitectureClick = useCallback(() => dispatch(openArchitectureModal()), [dispatch]);

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Architecture",
        id: "architecture",
        content: (
          <div style={{ textAlign: "left" }}>
            <ArchitecturePanel architecture={architecture} readOnly={readOnly} onChangeClick={handleOnChangeArchitectureClick} />
          </div>
        ),
        isComplete: architecture !== null,
      },
      {
        header: "Input Data",
        id: "inputData",
        content: <InputDataPanel readOnly={readOnly} inputs={extendedInputLayerGroupList} />,
        isComplete: layerKeys.includes("input_layers/demand") && layerKeys.includes("input_layers/streets"),
      },
      {
        header: "Data Prep",
        id: "dataPrep",
        content: <DataPrepPanel project={project} versionId={versionId} readOnly={readOnly} />,
        isComplete: dataPrepWorkflowState === "COMPLETE",
        isLoading: dataPrepWorkflowState === "STARTING" || dataPrepWorkflowState === "IN_PROGRESS",
      },
      {
        header: "Design",
        id: "design",
        content: <SolvePanel />,
        isComplete: versionStatus?.Status === StatusTypes.Complete && !versionStatus.IsDirty,
      },
      {
        header: "Artifacts",
        id: "artifacts",
        content: <ArtefactsPanel project={project} versionId={versionId} architecture={architecture} readOnly={readOnly} />,
        isComplete: layerKeys.includes(LayerIds.serviceArea),
        optional: true,
      },
      {
        header: "Reports",
        id: "reports",
        content: <ReportPanel reports={versionReports} project={project} versionId={versionId} readOnly={readOnly} />,
        isComplete: versionReports.length > 0,
        optional: true,
      },
    ],
    [
      architecture,
      readOnly,
      handleOnChangeArchitectureClick,
      layerKeys,
      project,
      versionId,
      versionStatus?.Status,
      versionStatus?.IsDirty,
      dataPrepWorkflowState,
      versionReports,
    ]
  );

  if (!project) return null;

  return (
    <Box data-testid="planner-extended-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? ( // if user has access and no license, more granular "locks" are applied per component
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <StackedNavigation
          rootComponent={<AccordionStepper panes={accordionPanes} id={project.ID} />}
          screens={extendedDesignStackedNavigationPanels}
        />
      )}
      {modal === Modals.architecture && (
        <ArchitectureModal onClose={() => handleOnArchitectureModalClose()} project={project} architecture={architecture} />
      )}
    </Box>
  );
};

export default ExtendedDesignPanel;
